/**
 * What the media endpoint will accept, checked before the bytes are sent.
 *
 * Mirrors `MAX_IMAGE_SIZE`, `MAX_VIDEO_SIZE`, `ALLOWED_IMAGE_MIME_TYPES` and
 * `ALLOWED_VIDEO_MIME_TYPES` in the backend's `global.constant.ts`. The server
 * enforces all of it regardless; doing it here first is what stops a 40MB file
 * being uploaded in full before being told no.
 *
 * Deliberately global rather than per-platform. Uploads are not addressed to a
 * platform — the backend renders 1:1, 4:5 and 1.91:1 derivatives from whatever
 * arrives, so a source image's own aspect ratio is not a reason to refuse it.
 */

const MB = 1024 * 1024;

export const MAX_IMAGE_BYTES = 10 * MB;
export const MAX_VIDEO_BYTES = 200 * MB;

export const ALLOWED_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
];
export const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime'];

/**
 * `accept` for a picker that takes images only, and one that also takes video.
 *
 * Mime types rather than extensions: the two pickers previously disagreed —
 * one allowed video and the other did not — and an extension list does not
 * describe what the server actually checks.
 */
export const ACCEPT_IMAGES = ALLOWED_IMAGE_MIME_TYPES.join(',');
export const ACCEPT_MEDIA = [
  ...ALLOWED_IMAGE_MIME_TYPES,
  ...ALLOWED_VIDEO_MIME_TYPES,
].join(',');

function formatBytes(bytes: number): string {
  return bytes >= MB
    ? `${Math.round(bytes / MB)}MB`
    : `${Math.round(bytes / 1024)}KB`;
}

/**
 * Why this file cannot be uploaded, or null when it can.
 *
 * Phrased for display: the message goes straight to the user, the same way the
 * backend's own rejections do.
 */
export function uploadRejectionReason(
  file: File,
  { allowVideo }: { allowVideo: boolean }
): string | null {
  const isImage = ALLOWED_IMAGE_MIME_TYPES.includes(file.type);
  const isVideo = ALLOWED_VIDEO_MIME_TYPES.includes(file.type);

  if (!isImage && !(allowVideo && isVideo)) {
    const accepted = allowVideo
      ? 'JPG, PNG, WebP, MP4 or MOV'
      : 'JPG, PNG or WebP';
    // `file.type` is empty for some drag sources, so say what is accepted
    // rather than naming a type we may not have.
    return file.type
      ? `${file.name} is ${file.type}. Uploads must be ${accepted}.`
      : `${file.name} is not a supported file. Uploads must be ${accepted}.`;
  }

  const limit = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  if (file.size > limit) {
    return `${file.name} is ${formatBytes(file.size)} and the limit is ${formatBytes(limit)}.`;
  }

  return null;
}
